import { useState } from 'react';
import { FiMenu, FiMoon, FiSun, FiX } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import useThemeSwitcher from '../../hooks/useThemeSwitcher';
import HireMeModal from '../HireMeModal';
import { motion } from 'framer-motion';
import Button from '../reusable/Button';

const AppHeader = () => {
	const [showMenu, setShowMenu] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [theme, setTheme, toggleTheme] = useThemeSwitcher();

	function toggleMenu() {
		if (!showMenu) {
			setShowMenu(true);
		} else {
			setShowMenu(false);
		}
	}

	function showHireMeModal() {
		if (!showModal) {
			document
				.getElementsByTagName('html')[0]
				.classList.add('overflow-y-hidden');
			setShowModal(true);
		} else {
			document
				.getElementsByTagName('html')[0]
				.classList.remove('overflow-y-hidden');
			setShowModal(false);
		}
	}

	return (
		<nav
			id="nav"
			className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 dark:bg-black/80 border-b border-black/[0.08] dark:border-white/[0.12] transition-colors duration-200"
		>
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex justify-between items-center">
				{/* Desktop Navigation Links - Apple HIG Style */}
				<div className="hidden sm:flex items-center space-x-1 lg:space-x-2">
					<Link
						to="/projects"
						className="text-[14px] font-medium text-slate-700 hover:text-sky-600 dark:text-slate-300 dark:hover:text-sky-400 px-3.5 py-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-all"
						aria-label="Projects"
					>
						Projects
					</Link>
					<Link
						to="/about"
						className="text-[14px] font-medium text-slate-700 hover:text-sky-600 dark:text-slate-300 dark:hover:text-sky-400 px-3.5 py-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-all"
						aria-label="About Me"
					>
						About Me
					</Link>
					<Link
						to="/blog"
						className="text-[14px] font-medium text-slate-700 hover:text-sky-600 dark:text-slate-300 dark:hover:text-sky-400 px-3.5 py-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-all"
						aria-label="Blog"
					>
						Blog
					</Link>
					<Link
						to="/contact"
						className="text-[14px] font-medium text-slate-700 hover:text-sky-600 dark:text-slate-300 dark:hover:text-sky-400 px-3.5 py-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/10 transition-all"
						aria-label="Contact"
					>
						Contact
					</Link>
				</div>

				{/* Right Actions: Chat with me Pill & Theme Switcher */}
				<div className="flex items-center space-x-3 ml-auto sm:ml-0">
					<a
						href="mailto:ethanweibiz@gmail.com"
						className="hidden sm:inline-flex items-center px-4 py-1.5 rounded-full bg-sky-600 hover:bg-sky-500 active:scale-95 text-white text-[13px] font-semibold shadow-sm transition-all"
						aria-label="Chat with me"
					>
						Chat with me
					</a>

					{/* Theme Switcher Button */}
					<button
						onClick={toggleTheme}
						aria-label="Theme Switcher"
						type="button"
						className="w-9 h-9 rounded-full flex items-center justify-center bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/20 active:scale-95 text-slate-700 dark:text-slate-200 transition-all cursor-pointer"
					>
						{theme === 'dark' ? (
							<FiSun className="text-amber-400 text-lg" />
						) : (
							<FiMoon className="text-slate-700 text-lg" />
						)}
					</button>

					{/* Mobile Hamburger Menu Toggle */}
					<div className="sm:hidden flex items-center">
						<button
							onClick={toggleMenu}
							type="button"
							className="p-2 rounded-lg text-slate-700 dark:text-slate-200 hover:bg-black/5 dark:hover:bg-white/10 focus:outline-none"
							aria-label="Toggle menu"
						>
							{showMenu ? <FiX className="text-2xl" /> : <FiMenu className="text-2xl" />}
						</button>
					</div>
				</div>
			</div>

			{/* Mobile Dropdown Menu - Frosted Glass Style */}
			{showMenu && (
				<div className="sm:hidden px-4 pt-2 pb-6 space-y-2 border-t border-black/[0.08] dark:border-white/[0.12] bg-white/95 dark:bg-black/95 backdrop-blur-xl">
					<Link
						to="/projects"
						onClick={() => setShowMenu(false)}
						className="block px-3 py-2 rounded-lg text-base font-medium text-slate-800 dark:text-slate-100 hover:bg-black/5 dark:hover:bg-white/10"
					>
						Projects
					</Link>
					<Link
						to="/about"
						onClick={() => setShowMenu(false)}
						className="block px-3 py-2 rounded-lg text-base font-medium text-slate-800 dark:text-slate-100 hover:bg-black/5 dark:hover:bg-white/10"
					>
						About Me
					</Link>
					<Link
						to="/blog"
						onClick={() => setShowMenu(false)}
						className="block px-3 py-2 rounded-lg text-base font-medium text-slate-800 dark:text-slate-100 hover:bg-black/5 dark:hover:bg-white/10"
					>
						Blog
					</Link>
					<Link
						to="/contact"
						onClick={() => setShowMenu(false)}
						className="block px-3 py-2 rounded-lg text-base font-medium text-slate-800 dark:text-slate-100 hover:bg-black/5 dark:hover:bg-white/10"
					>
						Contact
					</Link>
					<div className="pt-2">
						<a
							href="mailto:ethanweibiz@gmail.com"
							onClick={() => setShowMenu(false)}
							className="block w-full text-center px-4 py-2.5 rounded-full bg-sky-600 hover:bg-sky-500 text-white text-sm font-semibold shadow-sm"
							aria-label="Chat with me"
						>
							Chat with me
						</a>
					</div>
				</div>
			)}

			{/* Hire Me Modal */}
			{showModal && (
				<HireMeModal
					onClose={showHireMeModal}
					onRequest={showHireMeModal}
				/>
			)}
		</nav>
	);
};

export default AppHeader;
